import { FC, useCallback } from 'react';
import { useModal } from '@pcmp/react-providers';
import { useTranslation } from 'react-i18next';
import Button from '@ingka/button';
import filterIcon from '@ingka/ssr-icon/paths/filters';

import { Switch } from '@/components/controls/switch';
import CodesFilterSider from '@/components/siders/codes-filter';
import {
  useGetCodeFilters,
  useGetCountryFilter,
  useGetSiteFilter,
  useGetComponentFilter,
  useGetDepartmentFilter,
  useGetCarrierTypeFilter,
  useGetCarrierSizeFilter,
  useGetCommunicationPurposeFilter,
  useGetSubcategoryFilter,
} from '@/redux/features/code-filters/hooks';
import { useAppDispatch } from '@/redux/hooks';
import {
  setCountry,
  setSite,
  setOnlyMyCodes,
  setComponent,
  setDepartment,
  setCarrierType,
  setCarrierSize,
  setCommunicationPurpose,
  setSubcategory,
} from '@/redux/features/code-filters/slice';
import { DropdownCountries } from '@/components/controls/dropdown-countries';
import { DropdownSites } from '@/components/controls/dropdown-site';
import { DropdownComponents } from '@/components/controls/dropdown-component';
import { DropdownDepartment } from '@/components/controls/dropdown-department';
import { DropdownCarrierType } from '@/components/controls/dropdown-carrier-type';
import { DropdownCarrierSize } from '@/components/controls/dropdown-carrier-size';
import { DropdownCommunicationPurpose } from '@/components/controls/dropdown-communication-purpose';
import { DropdownSubcategory } from '@/components/controls/dropdown-subcategory';
import { Reset } from './reset';
import styles from './index.module.css';

export const CountriesFilter = () => {
  const dispatch = useAppDispatch();
  const country = useGetCountryFilter();
  const onChange = useCallback((value: string[]) => {
    dispatch(setCountry(value));
    dispatch(setSite([]));
  }, []);

  return <DropdownCountries value={country} onChange={onChange} confirm />;
};

export const SiteFilter = () => {
  const dispatch = useAppDispatch();
  const countries = useGetCountryFilter();
  const site = useGetSiteFilter();
  const onChange = useCallback((value: string[]) => {
    dispatch(setSite(value));
  }, []);

  return (
    <DropdownSites
      value={site}
      onChange={onChange}
      filterCountries={countries}
      confirm
    />
  );
};

export const ComponentFilter = () => {
  const dispatch = useAppDispatch();
  const components = useGetComponentFilter();
  const onChange = useCallback((value: string[]) => {
    dispatch(setComponent(value));
  }, []);
  return <DropdownComponents value={components} onChange={onChange} confirm />;
};

export const CommunicationPurposeFilter = () => {
  const dispatch = useAppDispatch();
  const communicationPurposes = useGetCommunicationPurposeFilter();
  const onChange = useCallback((value: string[]) => {
    dispatch(setCommunicationPurpose(value));
  }, []);
  return (
    <DropdownCommunicationPurpose
      value={communicationPurposes}
      onChange={onChange}
      confirm
    />
  );
};

export const SubcategoryFilter = () => {
  const dispatch = useAppDispatch();
  const subcategories = useGetSubcategoryFilter();
  const onChange = useCallback((value: string[]) => {
    dispatch(setSubcategory(value));
  }, []);
  return (
    <DropdownSubcategory value={subcategories} onChange={onChange} confirm />
  );
};

export const DepartmentFilter = () => {
  const dispatch = useAppDispatch();
  const departments = useGetDepartmentFilter();
  const onChange = useCallback((value: string[]) => {
    dispatch(setDepartment(value));
  }, []);
  return <DropdownDepartment value={departments} onChange={onChange} confirm />;
};

export const CarrierTypeFilter = () => {
  const dispatch = useAppDispatch();
  const carrierTypes = useGetCarrierTypeFilter();
  const onChange = useCallback((value: string[]) => {
    dispatch(setCarrierType(value));
  }, []);
  return (
    <DropdownCarrierType value={carrierTypes} onChange={onChange} confirm />
  );
};

export const CarrierSizeFilter = () => {
  const dispatch = useAppDispatch();
  const carrierSize = useGetCarrierSizeFilter();
  const onChange = useCallback((value: string[]) => {
    dispatch(setCarrierSize(value));
  }, []);
  return (
    <DropdownCarrierSize value={carrierSize} onChange={onChange} confirm />
  );
};

export const FilterBarDesktop: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { onlyMyCodes } = useGetCodeFilters();

  const handleOnlyMyCodes = (value: boolean) => {
    dispatch(setOnlyMyCodes(value));
  };

  return (
    <div
      className={`w-full gap-4 hidden sm:grid grid-flow-col sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 ${styles['filters']}`}
    >
      <div className="col-start-1 row-start-1 col-span-2 md:col-span-1">
        <Switch
          id="showOnlyMyCodes"
          data-testid="showOnlyMyCodes"
          label={t('label.code.myCodes')}
          value={onlyMyCodes}
          onChange={handleOnlyMyCodes}
        />
      </div>

      <div className="sm:row-start-2 sm:col-start-1 md:row-start-2 lg:col-start-2 md:justify-start lg:row-start-1 lg:col-start-2 lg:justify-center flex items-center ">
        <CountriesFilter />
      </div>
      <div className="sm:row-start-2 sm:col-start-2 sm:justify-center md:row-start-2 md:col-start-2 md:justify-center lg:row-start-1 lg:col-start-3 flex items-center">
        <SiteFilter />
      </div>
      <div className="sm:row-start-2 sm:col-start-3 sm:justify-end md:col-start-3 md:justify-center lg:row-start-1 lg:col-start-4 lg:row-start-1 flex items-center">
        <CommunicationPurposeFilter />
      </div>
      <div className="sm:row-start-3 sm:col-start-1 sm:justify-start md:row-start-2 md:col-start-4 md:justify-end lg:row-start-1 lg:col-start-5 flex items-center lg:justify-end">
        <SubcategoryFilter />
      </div>
      <div className="sm:row-start-3 sm:col-start-2 sm:justify-center md:row-start-3 md:col-start-1 md:justify-start flex items-center lg:row-start-2 lg:col-start-2 lg:justify-center">
        <ComponentFilter />
      </div>
      <div className="sm:row-start-3 sm:col-start-3 sm:justify-end md:row-start-3 md:col-start-2 md:justify-center flex items-center lg:row-start-2 lg:col-start-3 lg:justify-center">
        <DepartmentFilter />
      </div>
      <div className="sm:row-start-4 sm:col-start-1 sm:justify-start md:row-start-3 md:col-start-3 md:justify-center lg:row-start-2 lg:col-start-4 flex items-center">
        <CarrierTypeFilter />
      </div>
      <div className="sm:row-start-4 sm:col-start-2 sm:justify-center md:row-start-3 md:col-start-4 md:justify-end lg:row-start-2 lg:col-start-5 flex items-center">
        <CarrierSizeFilter />
      </div>
      <div className="sm:row-start-4 sm:col-start-3 sm:justify-end md:row-start-1 md:col-start-2 md:justify-center lg:row-start-2 lg:col-start-1 lg:justify-start flex items-center">
        <Reset />
      </div>
    </div>
  );
};

export const FilterBarMobile: FC = () => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const onOpenFilters = () => {
    openModal({
      component: CodesFilterSider,
      props: {},
    });
  };

  return (
    <div className="w-full flex justify-between items-center sm:hidden">
      <Button
        iconPosition="trailing"
        text={t('label.filter')}
        type="secondary"
        ssrIcon={filterIcon}
        fluid
        onClick={onOpenFilters}
      />
    </div>
  );
};
